import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { Slot } from "@radix-ui/react-slot";

import { cn } from "@/lib/utils";

const buttonVariants = cva(
	"focus-visible:border-ring focus-visible:ring-ring/50 aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive dark:aria-invalid:border-destructive/50 rounded-xl border border-transparent bg-clip-padding font-medium max-md:text-base md:text-lg 2xl:text-xl focus-visible:ring-3 aria-invalid:ring-3 [&_svg:not([class*='size-'])]:size-4 inline-flex items-center justify-center whitespace-nowrap transition-all disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none shrink-0 [&_svg]:shrink-0 outline-none group/button select-none",
	{
		variants: {
			colorMode: {
				light: "",
				dark: "",
				green: "",
				gold: "",
			},
			variant: {
				default:
					"bg-foreground text-background hover:bg-foreground/90 [a]:hover:bg-foreground/90",
				outline:
					"bg-transparent hover:bg-muted hover:text-foreground dark:bg-input/30 dark:border-input dark:hover:bg-input/50 aria-expanded:bg-muted aria-expanded:text-foreground",
				secondary:
					"bg-black text-white hover:bg-zinc-800 hover:text-white aria-expanded:bg-black aria-expanded:text-white",
				ghost: "",
				destructive:
					"bg-destructive/10 hover:bg-destructive/20 focus-visible:ring-destructive/20 dark:focus-visible:ring-destructive/40 dark:bg-destructive/20 text-destructive focus-visible:border-destructive/40 dark:hover:bg-destructive/30",
				link: "text-primary underline-offset-4 hover:underline p-0! max-md:text-sm md:text-md",
				"ghost-no-active":
					"hover:text-foreground dark:hover:bg-[#f1f0ee] aria-expanded:text-foreground",
			},
			size: {
				default:
					"h-8 md:h-9 2xl:h-10 gap-1.5 p-4 has-data-[icon=inline-end]:pr-3 has-data-[icon=inline-start]:pl-3",
				xs: "h-6 md:h-7 2xl:h-8 gap-1 rounded-[min(var(--radius-md),10px)] p-3 in-data-[slot=button-group]:rounded-lg has-data-[icon=inline-end]:pr-2.5 has-data-[icon=inline-start]:pl-2.5 [&_svg:not([class*='size-'])]:size-3",
				sm: "h-7 md:h-8 2xl:h-9 gap-1 rounded-[min(var(--radius-md),12px)] p-4 in-data-[slot=button-group]:rounded-lg has-data-[icon=inline-end]:pr-3 has-data-[icon=inline-start]:pl-3 [&_svg:not([class*='size-'])]:size-3.5",
				lg: "h-10 md:h-11 2xl:h-12 gap-1.5 p-5 has-data-[icon=inline-end]:pr-4 has-data-[icon=inline-start]:pl-4",
				xl: "max-md:text-base md:text-lg 4xl:text-xl h-12 md:h-13 2xl:h-14 gap-1.5 max-md:p-5 md:p-6 4xl:p-7 has-data-[icon=inline-end]:pr-5 has-data-[icon=inline-start]:pl-5",
				"2xl":
					"max-md:text-lg md:text-xl 4xl:text-2xl h-13 md:h-14 2xl:h-14 gap-2 max-md:p-6 md:p-7 4xl:p-8 has-data-[icon=inline-end]:pr-6 has-data-[icon=inline-start]:pl-6",
				"link-xl": "max-md:text-sm md:text-base 2xl:text-lg",
				icon: "size-8 md:size-9 2xl:size-10",
				"icon-xs":
					"size-6 md:size-7 2xl:size-8 rounded-[min(var(--radius-md),10px)] in-data-[slot=button-group]:rounded-lg [&_svg:not([class*='size-'])]:size-3",
				"icon-sm":
					"size-7 md:size-8 2xl:size-9 rounded-[min(var(--radius-md),12px)] in-data-[slot=button-group]:rounded-lg",
				"icon-lg": "size-9 md:size-10 2xl:size-11",
				"link-lg":
					"text-lg gap-1 text-foreground underline-offset-4 hover:underline p-0! text-base md:text-md",
			},
		},
		compoundVariants: [
			{
				colorMode: "light",
				variant: "outline",
				className: "border-[0.15em] border-neutral-900/80 text-foreground",
			},
			{
				colorMode: "light",
				variant: "ghost",
				className:
					"!text-foreground hover:bg-neutral-100 hover:!text-zinc-950 [a]:hover:!text-zinc-950 aria-expanded:bg-neutral-100 aria-expanded:!text-zinc-950",
			},
			{
				colorMode: "light",
				variant: "ghost-no-active",
				className:
					"!text-foreground hover:bg-neutral-100 hover:!text-zinc-950 [a]:hover:!text-zinc-950 aria-expanded:!text-zinc-950",
			},
			{
				colorMode: "dark",
				variant: "default",
				className:
					"bg-white text-zinc-950 hover:bg-zinc-100 [a]:hover:bg-zinc-100",
			},
			{
				colorMode: "dark",
				variant: "outline",
				className:
					"border-[0.15em] border-white bg-transparent text-white hover:bg-white/10 hover:text-white aria-expanded:bg-white/10 aria-expanded:text-white",
			},
			{
				colorMode: "dark",
				variant: "secondary",
				className:
					"bg-zinc-800 text-zinc-50 hover:bg-zinc-700 hover:text-white aria-expanded:bg-zinc-800 aria-expanded:text-zinc-50",
			},
			{
				colorMode: "dark",
				variant: "ghost",
				className:
					"!text-white/90 hover:bg-white/10 hover:!text-white [a]:hover:!text-white aria-expanded:bg-white/10 aria-expanded:!text-white",
			},
			{
				colorMode: "dark",
				variant: "ghost-no-active",
				className:
					"!text-white/90 hover:bg-white/10 hover:!text-white [a]:hover:!text-white aria-expanded:!text-white",
			},
			{
				colorMode: "dark",
				variant: "link",
				className: "text-white hover:text-white",
			},
			{
				colorMode: "dark",
				variant: "destructive",
				className:
					"border-destructive/20 bg-destructive/15 text-red-200 hover:bg-destructive/25",
			},
			{
				colorMode: "dark",
				size: "link-lg",
				className: "text-white",
			},
			{
				colorMode: "green",
				variant: "default",
				className:
					"bg-green-600 text-white hover:bg-green-500 [a]:hover:bg-green-500 focus-visible:ring-white/30",
			},
			{
				colorMode: "green",
				variant: "outline",
				className:
					"border-[0.12em] border-white bg-transparent text-white outline-offset-0 hover:bg-white/10 aria-expanded:border-white/90 aria-expanded:bg-white/10 dark:!border-white",
			},
			{
				colorMode: "green",
				variant: "secondary",
				className:
					"bg-green-800 text-white hover:bg-green-700 hover:text-white aria-expanded:bg-green-800 aria-expanded:text-white",
			},
			{
				colorMode: "green",
				variant: "ghost",
				className:
					"!text-white hover:!bg-white/10 hover:!text-white [a]:hover:!text-white aria-expanded:!text-white",
			},
			{
				colorMode: "green",
				variant: "ghost-no-active",
				className:
					"!text-white hover:!bg-white/10 hover:!text-white [a]:hover:!text-white aria-expanded:!text-white",
			},
			{
				colorMode: "green",
				variant: "link",
				className:
					// `green` scale is yellower than `emerald` (less teal); works on dark/purple sections
					"!m-0 !h-auto !min-h-0 !rounded-none !border-0 !bg-transparent !p-0 !shadow-none !text-green-300 underline decoration-2 decoration-green-400/85 underline-offset-4 hover:!text-green-200 hover:decoration-green-300 focus-visible:ring-2 focus-visible:ring-green-500/50 focus-visible:ring-offset-2 focus-visible:ring-offset-transparent [&_svg]:!text-green-300",
			},
			{
				colorMode: "green",
				variant: "link",
				size: "link-lg",
				className: "text-base md:text-lg 2xl:text-lg",
			},
			{
				colorMode: "green",
				variant: "destructive",
				className:
					"border border-red-300/30 bg-red-950/50 text-red-100 hover:border-red-300/50 hover:bg-red-950/80",
			},
			{
				colorMode: "gold",
				variant: "default",
				className:
					"bg-[var(--color-brand-gold)] text-zinc-950 hover:bg-[#b8a055] [a]:hover:bg-[#b8a055] focus-visible:ring-zinc-950/25",
			},
			{
				colorMode: "gold",
				variant: "outline",
				className:
					"border-[0.12em] border-[var(--color-brand-gold)]! bg-transparent! outline-offset-0 hover:bg-[color-mix(in_srgb,var(--color-brand-gold)_15%,transparent)]! aria-expanded:border-[var(--color-brand-gold)]! aria-expanded:bg-[color-mix(in_srgb,var(--color-brand-gold)_15%,transparent)]!",
			},
			{
				colorMode: "gold",
				variant: "secondary",
				className:
					"bg-[#a89252] text-zinc-950 hover:bg-[#967d47] [a]:hover:bg-[#967d47] hover:text-zinc-950 aria-expanded:bg-[#a89252] aria-expanded:text-zinc-950",
			},
			{
				colorMode: "gold",
				variant: "ghost",
				className:
					"!text-zinc-950 hover:!bg-[color-mix(in_srgb,var(--color-brand-gold)_20%,transparent)] hover:!text-zinc-950 [a]:hover:!text-zinc-950 aria-expanded:!bg-[color-mix(in_srgb,var(--color-brand-gold)_20%,transparent)] aria-expanded:!text-zinc-950",
			},
			{
				colorMode: "gold",
				variant: "ghost-no-active",
				className:
					"!text-zinc-950 hover:!bg-[color-mix(in_srgb,var(--color-brand-gold)_20%,transparent)] hover:!text-zinc-950 [a]:hover:!text-zinc-950 aria-expanded:!text-zinc-950",
			},
			{
				colorMode: "gold",
				variant: "link",
				className:
					"!m-0 !h-auto !min-h-0 !rounded-none !border-0 !bg-transparent !p-0 !shadow-none !text-[var(--color-brand-gold)] underline decoration-2 decoration-[color-mix(in_srgb,var(--color-brand-gold)_80%,transparent)] underline-offset-4 hover:!text-[color-mix(in_srgb,var(--color-brand-gold)_80%,transparent)] hover:decoration-[var(--color-brand-gold)] focus-visible:ring-2 focus-visible:ring-[color-mix(in_srgb,var(--color-brand-gold)_45%,transparent)] focus-visible:ring-offset-2 focus-visible:ring-offset-transparent [&_svg]:!text-[var(--color-brand-gold)]",
			},
			{
				colorMode: "gold",
				variant: "link",
				size: "link-lg",
				className: "text-base md:text-lg 2xl:text-lg",
			},
			{
				colorMode: "gold",
				variant: "destructive",
				className:
					"border border-red-600/25 bg-red-600/10 text-red-900 hover:border-red-600/40 hover:bg-red-600/15 dark:border-red-400/30 dark:bg-red-950/50 dark:text-red-100 dark:hover:bg-red-950/80",
			},
		],
		defaultVariants: {
			colorMode: "light",
			variant: "default",
			size: "default",
		},
	},
);

export interface ButtonProps
	extends
		React.ButtonHTMLAttributes<HTMLButtonElement>,
		VariantProps<typeof buttonVariants> {
	asChild?: boolean;
}

function Button({
	className,
	colorMode = "light",
	variant = "default",
	size = "default",
	asChild = false,
	...props
}: ButtonProps) {
	const Comp = asChild ? Slot : "button";

	return (
		<Comp
			data-slot="button"
			data-color-mode={colorMode}
			data-variant={variant}
			data-size={size}
			className={cn(buttonVariants({ colorMode, variant, size, className }))}
			{...props}
		/>
	);
}

export { Button, buttonVariants };
